import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { findCompanyBySlug } from '../../../client-experience/utils/companyAccess';
import { StorefrontAuthProvider } from '../../auth/context/StorefrontAuthContext';
import useCustomerLandingLightTheme from '../../hooks/useCustomerLandingLightTheme';
import { getLandingPageCustomStyles } from '../../utils/landingPageTheme';
import { prefetchStorefrontServices } from '../../storefront-booking/storefrontServicesCache';
import { warmStorefrontBusySlotsForToday } from '../../storefront-booking/warmStorefrontBusySlots';
import Header from '../Header';
import StorefrontPoweredByFooter from '../storefront-powered-by-footer/StorefrontPoweredByFooter';
import CompanyNotFoundView from '../company-not-found/CompanyNotFoundView';
import { PageSkeleton } from '../../../components/loading/skeletons';
import '../../styles/LandingPage.css';

const isStorefrontLoginPath = (pathname) => /\/book\/[^/]+\/login\/?$/.test(pathname || '');
const isStorefrontBookingFlowPath = (pathname, search = '') => {
  if (pathname && pathname.includes('/book-appointment')) return true;
  try {
    const q = search && search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(q);
    if (!params.has('book')) return false;
    const raw = params.get('book');
    if (raw === '0' || raw === 'false') return false;
    return true;
  } catch {
    return false;
  }
};

const CustomerLandingLayout = () => {
  const { companyUrl } = useParams();
  const location = useLocation();
  const prevLocationRef = useRef(location);
  useCustomerLandingLightTheme();

  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        setError(null);

        const company = await findCompanyBySlug(companyUrl);
        if (!company) {
          setCompanyData(null);
          setError('Company not found');
          return;
        }

        setCompanyData(company);
        prefetchStorefrontServices(company.id);
        void warmStorefrontBusySlotsForToday(company.id);
      } catch (err) {
        console.error('Error fetching company data:', err);
        setCompanyData(null);
        setError(err?.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    if (companyUrl) {
      fetchCompany();
    }
  }, [companyUrl]);

  useEffect(() => {
    const prev = prevLocationRef.current;
    const next = location;

    // Avoid snapping to top when swapping between booking flow and login — keeps SPA transitions smooth.
    if (
      (isStorefrontLoginPath(prev.pathname) && isStorefrontBookingFlowPath(next.pathname, next.search))
      || (isStorefrontBookingFlowPath(prev.pathname, prev.search) && isStorefrontLoginPath(next.pathname))
    ) {
      prevLocationRef.current = location;
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    prevLocationRef.current = location;
  }, [location]);

  const customStyles = useMemo(
    () => getLandingPageCustomStyles(companyData?.landingPageSettings),
    [companyData]
  );

  if (loading) {
    return (
      <div className="landing-page">
        <PageSkeleton padded variant="cards" label="Loading storefront" />
      </div>
    );
  }

  if (error || !companyData) {
    return (
      <div className="landing-page">
        <div className="error-container error-container--storefront-missing">
          <CompanyNotFoundView attemptedSlug={companyUrl} technicalError={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page" style={customStyles}>
      <StorefrontAuthProvider companyData={companyData}>
        <Header companyData={companyData} />
        <Outlet context={{ companyData }} />
        <StorefrontPoweredByFooter />
      </StorefrontAuthProvider>
    </div>
  );
};

export default CustomerLandingLayout;
